import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Store, MapPin, Phone, Mail, Clock, Truck, Save, Image, ArrowLeft,
  Plus, X, ChevronDown, ChevronUp, Check, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { ImageUpload } from '../../components/ImageUpload';
import { toast } from 'sonner';

const CUISINE_TYPES = [
  'Italian', 'Chinese', 'Indian', 'Mexican', 'Japanese', 'Thai', 'Greek',
  'French', 'Spanish', 'American', 'British', 'Turkish', 'Korean', 'Vietnamese',
  'Lebanese', 'Moroccan', 'Caribbean', 'Ethiopian', 'Brazilian', 'Peruvian',
  'Fast Food', 'Pizza', 'Burgers', 'Sushi', 'Seafood', 'Vegan', 'Vegetarian',
  'Desserts', 'Cafe', 'Bakery', 'Halal', 'Kosher', 'Gluten-Free', 'Other'
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const defaultHours = {
  monday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
  tuesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
  wednesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
  thursday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
  friday: { slots: [{ open: '09:00', close: '23:00' }], closed: false },
  saturday: { slots: [{ open: '10:00', close: '23:00' }], closed: false },
  sunday: { slots: [{ open: '10:00', close: '22:00' }], closed: false },
};

export default function SalesAddRestaurant() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showHours, setShowHours] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cuisine_type: [],
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    latitude: null,
    longitude: null,
    delivery_radius_km: 10,
    minimum_order: 10,
    delivery_fee: 2.50,
    image_url: '',
    opening_hours: defaultHours
  });

  const [menuItems, setMenuItems] = useState([]);
  const [newMenuItem, setNewMenuItem] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCuisineToggle = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      cuisine_type: prev.cuisine_type.includes(cuisine)
        ? prev.cuisine_type.filter(c => c !== cuisine)
        : [...prev.cuisine_type, cuisine]
    }));
  };

  const handleAddressSelect = (address) => {
    if (address) {
      setFormData(prev => ({
        ...prev,
        address: address.address || '',
        latitude: address.latitude,
        longitude: address.longitude,
        city: address.components?.city || prev.city,
        postal_code: address.components?.postalCode || prev.postal_code
      }));
    }
  };

  const handleHoursChange = (day, field, value, slotIndex = 0) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: field === 'closed' 
          ? { ...prev.opening_hours[day], closed: value }
          : {
              ...prev.opening_hours[day],
              slots: prev.opening_hours[day].slots.map((slot, i) => 
                i === slotIndex ? { ...slot, [field]: value } : slot
              )
            }
      }
    }));
  };

  const addMenuItem = () => {
    if (!newMenuItem.name || !newMenuItem.price || !newMenuItem.category) {
      toast.error('Please fill in name, price, and category');
      return;
    }
    setMenuItems(prev => [...prev, { ...newMenuItem, id: Date.now(), price: parseFloat(newMenuItem.price) }]);
    setNewMenuItem({ name: '', description: '', price: '', category: '', image_url: '' });
  };

  const removeMenuItem = (id) => {
    setMenuItems(prev => prev.filter(item => item.id !== id));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.address || !formData.city || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (formData.cuisine_type.length === 0) {
      toast.error('Please select at least one cuisine type');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep1()) {
      setStep(1);
      return;
    }

    setLoading(true);
    try {
      // Create the restaurant
      const restaurantResponse = await api.post('/restaurants', {
        ...formData,
        cuisine_type: formData.cuisine_type
      });

      const restaurantId = restaurantResponse.data.restaurant_id;

      // Add menu items if any
      if (menuItems.length > 0) {
        for (const item of menuItems) {
          try {
            await api.post(`/restaurants/${restaurantId}/menu`, {
              name: item.name,
              description: item.description || '',
              price: item.price,
              category: item.category,
              image_url: item.image_url || null,
              is_available: true,
              allergens: [],
              dietary_info: []
            });
          } catch (e) {
            console.error('Failed to add menu item:', e);
          }
        }
      }

      toast.success('Restaurant created successfully!');
      navigate('/sales/restaurants');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create restaurant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sales-add-restaurant">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/sales/restaurants')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add New Restaurant</h1>
          <p className="text-slate-500">Onboard a new restaurant to Hambre</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => setStep(1)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            step === 1 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
            step === 1 ? 'bg-purple-500 text-white' : 'bg-slate-300 text-white'
          }`}>1</span>
          Restaurant Details
        </button>
        <div className="w-12 h-0.5 bg-slate-200" />
        <button
          onClick={() => validateStep1() && setStep(2)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            step === 2 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
            step === 2 ? 'bg-purple-500 text-white' : 'bg-slate-300 text-white'
          }`}>2</span>
          Menu Items
        </button>
      </div>

      {/* Step 1: Restaurant Details */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-purple-500" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Restaurant Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter restaurant name"
                    data-testid="restaurant-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="restaurant@email.com"
                    data-testid="restaurant-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe the restaurant..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Cuisine Types * (select at least one)</Label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_TYPES.map(cuisine => (
                    <button
                      key={cuisine}
                      type="button"
                      onClick={() => handleCuisineToggle(cuisine)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.cuisine_type.includes(cuisine)
                          ? 'bg-purple-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {formData.cuisine_type.includes(cuisine) && <Check className="w-3 h-3 inline mr-1" />}
                      {cuisine}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Restaurant Image</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => handleInputChange('image_url', url)}
                  endpoint="/upload/image"
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-500" />
                Location & Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Address *</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => handleInputChange('address', value)}
                  onSelect={handleAddressSelect}
                  placeholder="Start typing address..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                    data-testid="restaurant-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Postal Code</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    placeholder="Postal code"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+44 1234 567890"
                  data-testid="restaurant-phone"
                />
              </div>
            </CardContent>
          </Card>

          {/* Delivery Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-500" />
                Delivery Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Radius (km)</Label>
                  <Input
                    type="number"
                    value={formData.delivery_radius_km}
                    onChange={(e) => handleInputChange('delivery_radius_km', parseFloat(e.target.value) || 0)}
                    min="1"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Order (£)</Label>
                  <Input
                    type="number"
                    value={formData.minimum_order}
                    onChange={(e) => handleInputChange('minimum_order', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Delivery Fee (£)</Label>
                  <Input
                    type="number"
                    value={formData.delivery_fee}
                    onChange={(e) => handleInputChange('delivery_fee', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.50"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Opening Hours */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setShowHours(!showHours)}>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-500" />
                  Opening Hours
                </CardTitle>
                {showHours ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
              <CardDescription>Click to {showHours ? 'collapse' : 'expand'}</CardDescription>
            </CardHeader>
            {showHours && (
              <CardContent className="space-y-4">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-4 py-2 border-b last:border-0">
                    <div className="w-28 font-medium capitalize">{day}</div>
                    <Switch
                      checked={!formData.opening_hours[day]?.closed}
                      onCheckedChange={(checked) => handleHoursChange(day, 'closed', !checked)}
                    />
                    {!formData.opening_hours[day]?.closed && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={formData.opening_hours[day]?.slots?.[0]?.open || '09:00'}
                          onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                          className="w-32"
                        />
                        <span className="text-slate-500">to</span>
                        <Input
                          type="time"
                          value={formData.opening_hours[day]?.slots?.[0]?.close || '22:00'}
                          onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                          className="w-32"
                        />
                      </div>
                    )}
                    {formData.opening_hours[day]?.closed && (
                      <span className="text-slate-500">Closed</span>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => validateStep1() && setStep(2)} className="bg-purple-500 hover:bg-purple-600">
              Continue to Menu
              <ChevronDown className="w-4 h-4 ml-2 rotate-[-90deg]" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Menu Items */}
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Add Menu Items</CardTitle>
              <CardDescription>
                Add menu items now or skip - the restaurant can add them later
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add Item Form */}
              <div className="bg-slate-50 p-4 rounded-xl space-y-4">
                <h4 className="font-medium text-slate-900">New Menu Item</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Item Name *</Label>
                    <Input
                      value={newMenuItem.name}
                      onChange={(e) => setNewMenuItem(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Margherita Pizza"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Input
                      value={newMenuItem.category}
                      onChange={(e) => setNewMenuItem(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g., Pizza, Starters, Mains"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price (£) *</Label>
                    <Input
                      type="number"
                      value={newMenuItem.price}
                      onChange={(e) => setNewMenuItem(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newMenuItem.description}
                      onChange={(e) => setNewMenuItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Item description"
                    />
                  </div>
                </div>
                <Button type="button" onClick={addMenuItem} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              {/* Menu Items List */}
              {menuItems.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">Added Items ({menuItems.length})</h4>
                  <div className="space-y-2">
                    {menuItems.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-slate-500">{item.category} • £{item.price.toFixed(2)}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeMenuItem(item.id)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSubmit} disabled={loading}>
                Skip Menu & Create
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-purple-500 hover:bg-purple-600">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Restaurant
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
